import { Link } from "react-router-dom";
import { useObserver, useSubtree } from "subtree.js/react";
import { formatCents, formatMultiplier, MULTIPLIER_PRESETS } from "../../core/primitives/live";
import { SellBoxActions, SellBoxState } from "./sell-box-model";

/**
 * The one-page setup for one box: what is being broken, the spots, the money,
 * the cover. Every number on screen is `plan`, derived
 * by the controller from the draft the way the server prices it.
 */
export function SellBoxPage() {
  const state = useSubtree(SellBoxState);
  const actions = useSubtree(SellBoxActions);
  const observer = useObserver();

  const store = observer.watch(state.store);
  const paymentsReady = observer.watch(state.paymentsReady);
  const draft = observer.watch(state.draft);
  const templates = observer.watch(state.templates);
  const templatesStatus = observer.watch(state.templatesStatus);
  const pasteOpen = observer.watch(state.pasteOpen);
  const pastedSpots = observer.watch(state.pastedSpots);
  const parsed = observer.watch(state.parsed);
  const parse = observer.watch(state.parse);
  const cover = observer.watch(state.cover);
  const create = observer.watch(state.create);
  const plan = observer.watch(state.plan);
  const sealedProducts = observer.watch(state.sealedProducts);
  const sealedStatus = observer.watch(state.sealedStatus);
  const listName = observer.watch(state.listName);
  const saveList = observer.watch(state.saveList);

  const { labels, projection, costCents, usingPaste, evenlyPriced, spotRows } = plan;
  const pickedProduct = draft.sealedProductId
    ? (sealedProducts.find((p) => p.id === draft.sealedProductId) ?? null)
    : null;

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4">
      {/* Said up front rather than at Publish: spots go on sale the moment a
          stream is created, so the backend refuses the whole stream until the shop
          can take money, and the host should hear that before filling the form. */}
      {!paymentsReady && (
        <p className="note note-warn">
          This shop cannot take payments yet. You can plan the stream now, but spots go on sale as soon as it is
          published, so it cannot be published until Stripe is connected.{" "}
          {store && (
            <Link to={`/stores/${store.id}/settings`} className="underline">
              Connect Stripe
            </Link>
          )}
        </p>
      )}

      <header>
        <h1 className="text-xl font-semibold">Put a box on sale</h1>
        <p className="text-sm opacity-60">
          Choose what you are breaking, set the price, publish. Spots go on sale straight away and the box waits in your
          queue until you go live and break it.
        </p>
      </header>

      <section className="panel space-y-3 p-4">
        <div className="panel-head">What are you breaking?</div>

        {/* Inventory first: a box picked here fills the name and the money and
            comes off the shelf while the break is open. Typing is still allowed
            for product the shop never counted. */}
        {(sealedProducts.length > 0 || sealedStatus.kind === "loading") && (
          <label className="block">
            <span className="label">From inventory</span>
            <select
              className="field"
              value={draft.sealedProductId ?? ""}
              onChange={(e) => actions.pickSealedProduct(e.target.value || null)}
              disabled={sealedStatus.kind === "loading"}
            >
              <option value="">{sealedStatus.kind === "loading" ? "Loading…" : "Not from inventory"}</option>
              {sealedProducts.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} — {p.available} on hand
                </option>
              ))}
            </select>
            {pickedProduct && (
              <span className="mt-1 block text-xs opacity-50">
                {pickedProduct.available} on hand. One box is held for this break and leaves stock when it finishes.
              </span>
            )}
          </label>
        )}
        {sealedProducts.length === 0 && sealedStatus.kind === "loaded" && (
          <p className="text-xs opacity-50">
            Nothing in stock to pick from.{" "}
            <Link to="/sealed" className="underline">
              Add sealed product
            </Link>{" "}
            to have it come off the shelf as you break it.
          </p>
        )}

        <label className="block">
          <span className="label">Name</span>
          <input
            className="field"
            value={draft.title}
            onChange={(e) => actions.setTitle(e.target.value)}
            placeholder="2025 Prizm Basketball Hobby Box"
            maxLength={200}
          />
          <span className="mt-1 block text-xs opacity-50">Buyers see it on the break tile.</span>
        </label>
      </section>

      <section className="panel space-y-3 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="panel-head">The spots</div>
          <span className="text-xs opacity-60 tabular-nums">
            {labels.length === 0 ? "Choose a spot list" : `${labels.length} spots`}
          </span>
        </div>

        {/* The lists, by name and by size: one tap that says what it is. */}
        <div className="flex flex-wrap gap-2">
          {templates.map((option) => (
            <span key={option.key} className="inline-flex items-center">
              <button
                type="button"
                className={`btn-chip ${option.key === draft.templateKey && !usingPaste ? "btn-chip-on" : ""}`}
                onClick={() => actions.pickTemplate(option.key)}
              >
                {option.name}
                {option.source === "shop" && <span className="ml-1 opacity-50">· {option.labels.length}</span>}
              </button>
              {/* Only the shop's own can go; the leagues and the curated library are everyone's. */}
              {option.id && option.source === "shop" && (
                <button
                  type="button"
                  className="btn-ghost btn-xs ml-0.5"
                  aria-label={`Remove ${option.name}`}
                  onClick={() => void actions.removeList(option.id as string)}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          <button
            type="button"
            className={`btn-chip ${usingPaste || pasteOpen ? "btn-chip-on" : ""}`}
            onClick={actions.togglePaste}
          >
            Custom list
          </button>
        </div>

        {templatesStatus.kind === "loading" && <p className="note">Loading the lists…</p>}

        {pasteOpen && (
          <div className="space-y-2">
            <label className="block">
              <span className="label">Paste a spot list</span>
              <textarea
                className="field min-h-32 font-mono text-sm"
                value={draft.spotText}
                onChange={(e) => actions.setSpotText(e.target.value)}
                placeholder={"Charizard\t120\nPikachu\t45\nPidgey"}
              />
              <span className="mt-1 block text-xs opacity-50">
                One per line, straight out of a spreadsheet. Name first, then an optional price, then an optional note
                only you see. A header row is recognised and dropped.
              </span>
            </label>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={draft.spotText.trim() === "" || parse.busy}
                onClick={actions.readSpots}
              >
                {parse.busy ? "Reading…" : "Read these spots"}
              </button>
              <label className="btn-secondary btn-sm cursor-pointer">
                Upload a file
                <input
                  type="file"
                  accept=".csv,.tsv,.txt,text/csv,text/plain"
                  className="hidden"
                  disabled={parse.busy}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void actions.uploadList(file);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
            <span className="block text-xs opacity-50">
              A CSV or text file, one spot per line. Excel: save as CSV first.
            </span>
          </div>
        )}

        {/* Said out loud rather than swallowed: line 41 had no name and line 88
            repeats line 12 here, not mid-stream. */}
        {parsed?.errors.map((message) => (
          <p key={message} className="note note-danger">
            {message}
          </p>
        ))}
        {parsed?.warnings.slice(0, 5).map((message) => (
          <p key={message} className="note note-warn">
            {message}
          </p>
        ))}
        {parse.error && <p className="note note-danger">{parse.error}</p>}

        {usingPaste && (
          <p className="note note-accent">
            Using your list: {pastedSpots.length} spots
            {parsed?.headerSkipped ? ", header row dropped" : ""}.
          </p>
        )}

        {/* Kept for next time: the same names become a chip beside the leagues. */}
        {usingPaste && (
          <div className="flex flex-wrap items-end gap-2">
            <label className="block flex-1">
              <span className="label">Save this list as</span>
              <input
                className="field"
                value={listName}
                onChange={(e) => actions.setListName(e.target.value)}
                placeholder="Pokémon 151"
                maxLength={120}
              />
            </label>
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={listName.trim() === "" || saveList.busy}
              onClick={() => void actions.saveList()}
            >
              {saveList.busy ? "Saving…" : "Save list"}
            </button>
            {saveList.error && <p className="note note-danger w-full">{saveList.error}</p>}
          </div>
        )}

        {labels.length > 0 && evenlyPriced && projection && (
          <p className="text-xs opacity-60">Every spot is {formatCents(projection.pricePerSpot)}.</p>
        )}

        {/* The list itself, capped and scrolled: three hundred spots must not
            push the money off the screen. */}
        {labels.length > 0 && (
          <div className="max-h-64 overflow-y-auto rounded-row border border-line">
            <table className="w-full text-sm">
              <tbody>
                {spotRows.map((row) => (
                  <tr key={row.slot} className="border-b border-line/50 last:border-b-0">
                    <td className="w-10 px-2 py-1 text-xs opacity-50 tabular-nums">{row.slot}</td>
                    <td className="px-2 py-1">{row.label}</td>
                    <td className="w-24 px-2 py-1 text-right text-xs tabular-nums">
                      {row.priceCents !== null ? formatCents(row.priceCents) : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel space-y-3 p-4">
        <div className="panel-head">The money</div>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="label">What the box cost you</span>
            <input
              className="field"
              inputMode="decimal"
              value={draft.cost}
              onChange={(e) => actions.setCost(e.target.value)}
              placeholder="$400"
            />
            <span className="mt-1 block text-xs opacity-50">Private. Buyers never see this.</span>
          </label>

          <label className="block">
            <span className="label">What it is worth (optional)</span>
            <input
              className="field"
              inputMode="decimal"
              value={draft.marketValue}
              onChange={(e) => actions.setMarketValue(e.target.value)}
              placeholder={costCents > 0 ? formatCents(costCents) : "$600"}
            />
            <span className="mt-1 block text-xs opacity-50">
              Market value of the box, also private. Leave it blank and we price off what you paid.
            </span>
          </label>
        </div>

        <div>
          <span className="label">Target</span>
          <div className="mt-1 flex flex-wrap gap-2">
            {MULTIPLIER_PRESETS.map((pct) => (
              <button
                key={pct}
                type="button"
                className={`btn-chip ${pct === draft.multiplier ? "btn-chip-on" : ""}`}
                onClick={() => actions.setMultiplier(pct)}
              >
                {formatMultiplier(pct)}
              </button>
            ))}
          </div>
        </div>

        {projection && (
          <div className="note note-accent space-y-1">
            <div className="flex justify-between">
              <span>Price per spot</span>
              <strong>{formatCents(projection.pricePerSpot)}</strong>
            </div>
            <div className="flex justify-between opacity-70">
              <span>If every spot sells</span>
              <span>{formatCents(projection.gross)}</span>
            </div>
            <div className="flex justify-between opacity-70">
              <span>What you paid</span>
              <span>{formatCents(costCents)}</span>
            </div>
            <div className="flex justify-between opacity-70">
              <span>Profit</span>
              <span>{formatCents(projection.profit)}</span>
            </div>
          </div>
        )}

        {projection && projection.profit < 0 && (
          <p className="note note-warn">
            Even if every spot sells, this does not cover what you paid. Raise the target or lower the cost.
          </p>
        )}
      </section>

      <section className="panel space-y-3 p-4">
        <div className="panel-head">Tile image</div>
        <p className="text-sm opacity-60">
          What buyers see on the break tile. Required; a product picked from inventory brings its own.
        </p>

        <div className="flex items-center gap-3">
          {draft.coverImageUrl && <img src={draft.coverImageUrl} alt="" className="h-16 w-16 rounded object-cover" />}
          <label className="btn-secondary btn-sm cursor-pointer">
            {cover.busy ? "Uploading…" : draft.coverImageUrl ? "Replace" : "Choose an image"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={cover.busy}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void actions.uploadCover(file);
              }}
            />
          </label>
          {draft.coverImageUrl && (
            <button type="button" className="btn-ghost btn-xs" onClick={actions.removeCover}>
              Remove
            </button>
          )}
        </div>

        {cover.error && <p className="note note-danger">{cover.error}</p>}
      </section>

      <section className="panel space-y-3 p-4">
        <div className="panel-head">Break time</div>
        <p className="text-sm opacity-60">
          Optional. Not earlier than this: buyers see it and are told when you go live, and drawing earlier asks you
          first. Once a spot sells it moves later freely, earlier only to two hours away.
        </p>
        <input
          type="datetime-local"
          className="field w-auto"
          value={draft.breakTime}
          onChange={(e) => actions.setBreakTime(e.target.value)}
        />
      </section>

      {create.error && <p className="note note-danger">{create.error}</p>}
      {!draft.coverImageUrl && <p className="text-sm opacity-60">Add a tile image to put the box on sale.</p>}

      <div className="flex justify-end gap-2 pb-8">
        <button type="button" className="btn-primary" disabled={!plan.ready || create.busy} onClick={actions.create}>
          {create.busy ? "Putting on sale…" : "Put on sale"}
        </button>
      </div>
    </div>
  );
}
